/**
 * Layout Label Popup
 *
 * Floating popup for editing, moving, or deleting a layout label on the map.
 * Also handles creating a new label when the user clicks in "add label" mode.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useUIStore } from '../../stores/uiStore';
import { useProjectStore } from '../../stores/projectStore';
import { useMapStore } from '../../stores/mapStore';
import { transformPoint, inverseTransformPoint } from '../../domain/layout/transform';
import { classifyLabel } from '../../domain/layout/classify';
import type { LayoutTextEntity, StringOverride } from '../../domain/layout/types';
import { StringGridControls } from './StringGridControls';
import './LayoutLabelPopup.css';

const LEVEL_COLORS: Record<string, string> = {
  string: '#ffffff',
  inverter: '#3b82f6',
  mppt: '#06b6d4',
  dccb: '#f97316',
  station: '#ef4444',
  module: '#a855f7',
  row: '#ec4899',
  zone: '#14b8a6',
  infrastructure: '#9ca3af',
  unclassified: '#fbbf24',
};

const STANDARD_LEVEL_OPTIONS: { value: string; label: string }[] = [
  { value: 'station', label: 'Station' },
  { value: 'inverter', label: 'Inverter' },
  { value: 'mppt', label: 'MPPT' },
  { value: 'dccb', label: 'DCCB' },
  { value: 'string', label: 'String' },
  { value: 'module', label: 'Module' },
  { value: 'row', label: 'Row' },
  { value: 'zone', label: 'Zone' },
];

let labelCounter = 0;

export default function LayoutLabelPopup() {
  const entityId = useUIStore((s) => s.selectedLayoutEntityId);
  const selectedEntityIds = useUIStore((s) => s.selectedLayoutEntityIds);
  const moveMode = useUIStore((s) => s.layoutLabelMoveMode);
  const newLabelPosition = useUIStore((s) => s.newLabelPosition);
  const selectLayoutEntity = useUIStore((s) => s.selectLayoutEntity);
  const selectLayoutEntities = useUIStore((s) => s.selectLayoutEntities);
  const setLayoutLabelMoveMode = useUIStore((s) => s.setLayoutLabelMoveMode);
  const setNewLabelPosition = useUIStore((s) => s.setNewLabelPosition);
  const showConfirm = useUIStore((s) => s.showConfirm);

  const siteLayout = useProjectStore((s) => s.data.siteLayout);

  // Combine standard + custom levels from schemas
  const allLevelOptions = useMemo(() => {
    const opts = [...STANDARD_LEVEL_OPTIONS];
    const customColors = siteLayout?.customLevelColors ?? {};
    const stdValues = new Set(STANDARD_LEVEL_OPTIONS.map((o) => o.value));
    for (const key of Object.keys(customColors)) {
      if (!stdValues.has(key)) {
        opts.push({ value: key, label: key.charAt(0).toUpperCase() + key.slice(1) });
      }
    }
    return opts;
  }, [siteLayout?.customLevelColors]);

  const getLevelColor = useCallback((level: string) => {
    return LEVEL_COLORS[level] ?? siteLayout?.customLevelColors?.[level] ?? '#9ca3af';
  }, [siteLayout?.customLevelColors]);

  const addTextEntity = useProjectStore((s) => s.addTextEntity);
  const removeTextEntity = useProjectStore((s) => s.removeTextEntity);
  const removeTextEntities = useProjectStore((s) => s.removeTextEntities);
  const updateTextEntity = useProjectStore((s) => s.updateTextEntity);
  const batchSetLabelLevel = useProjectStore((s) => s.batchSetLabelLevel);
  const batchPatchStringOverrides = useProjectStore((s) => s.batchPatchStringOverrides);
  const batchRemoveStringOverrides = useProjectStore((s) => s.batchRemoveStringOverrides);
  const setStringOverride = useProjectStore((s) => s.setStringOverride);
  const removeStringOverride = useProjectStore((s) => s.removeStringOverride);

  const map = useMapStore((s) => s.map);

  const [editText, setEditText] = useState('');
  const [screenPos, setScreenPos] = useState<{ x: number; y: number } | null>(null);
  // Module Grid Override section: collapsed by default (owner 2026-09-02);
  // a badge keeps an overridden string visible while collapsed.
  const [gridOpen, setGridOpen] = useState(false);
  useEffect(() => { setGridOpen(false); }, [entityId]);

  // New label creation state
  const [newLabelText, setNewLabelText] = useState('');
  const [newLabelLevel, setNewLabelLevel] = useState('string');
  const [newScreenPos, setNewScreenPos] = useState<{ x: number; y: number } | null>(null);

  // ── EDIT MODE: existing entity ──
  const entity: LayoutTextEntity | undefined = siteLayout?.textEntities.find((e) => e.id === entityId);

  useEffect(() => {
    if (!entity || !siteLayout?.transform || !map) {
      setScreenPos(null);
      return;
    }
    const [lng, lat] = transformPoint(siteLayout.transform, entity.cx, entity.cy);
    const pt = map.project([lng, lat]);
    setScreenPos({ x: pt.x, y: pt.y });
    setEditText(entity.normalizedText);
  }, [entity, siteLayout?.transform, map]);

  useEffect(() => {
    if (!entity || !siteLayout?.transform || !map) return;
    function onMove() {
      if (!entity || !siteLayout?.transform || !map) return;
      const [lng, lat] = transformPoint(siteLayout.transform, entity.cx, entity.cy);
      const pt = map.project([lng, lat]);
      setScreenPos({ x: pt.x, y: pt.y });
    }
    map.on('move', onMove);
    return () => { map.off('move', onMove); };
  }, [entity, siteLayout?.transform, map]);

  const handleMoveClick = useCallback((e: mapboxgl.MapMouseEvent) => {
    if (!entityId || !siteLayout?.transform) return;
    e.preventDefault();
    const { lng, lat } = e.lngLat;
    const [pdfX, pdfY] = inverseTransformPoint(siteLayout.transform, lng, lat);
    updateTextEntity(entityId, { cx: pdfX, cy: pdfY, manuallyMoved: true });
    setLayoutLabelMoveMode(false);
  }, [entityId, siteLayout?.transform, updateTextEntity, setLayoutLabelMoveMode]);

  useEffect(() => {
    if (!moveMode || !map) return;
    map.getCanvas().style.cursor = 'crosshair';
    map.once('click', handleMoveClick);
    return () => {
      map.off('click', handleMoveClick);
      map.getCanvas().style.cursor = '';
    };
  }, [moveMode, map, handleMoveClick]);

  // ── NEW LABEL MODE: position from add-label click ──
  useEffect(() => {
    if (!newLabelPosition || !map) {
      setNewScreenPos(null);
      return;
    }
    const pt = map.project([newLabelPosition.lng, newLabelPosition.lat]);
    setNewScreenPos({ x: pt.x, y: pt.y });
    setNewLabelText('');
    setNewLabelLevel('string');
  }, [newLabelPosition, map]);

  useEffect(() => {
    if (!newLabelPosition || !map) return;
    function onMove() {
      if (!newLabelPosition || !map) return;
      const pt = map.project([newLabelPosition.lng, newLabelPosition.lat]);
      setNewScreenPos({ x: pt.x, y: pt.y });
    }
    map.on('move', onMove);
    return () => { map.off('move', onMove); };
  }, [newLabelPosition, map]);

  // ── Render: New label creation popup ──
  if (newLabelPosition && newScreenPos && siteLayout?.transform) {
    function handleCreate() {
      if (!newLabelText.trim() || !siteLayout?.transform || !newLabelPosition) return;
      const [pdfX, pdfY] = inverseTransformPoint(siteLayout.transform, newLabelPosition.lng, newLabelPosition.lat);
      const text = newLabelText.trim();
      const classification = classifyLabel(text, siteLayout?.classificationConfig);
      const newEntity: LayoutTextEntity = {
        id: `manual-${Date.now()}-${++labelCounter}`,
        text,
        normalizedText: text,
        cx: pdfX,
        cy: pdfY,
        width: 0,
        height: 0,
        rotation: 0,
        fontSize: 10,
        classification: classification ?? {
          level: newLabelLevel,
          value: text,
        },
      };
      // If classifyLabel didn't match, use the user-selected level
      if (!classification) {
        newEntity.classification = { level: newLabelLevel, value: text };
      }
      addTextEntity(newEntity);
      setNewLabelPosition(null);
    }

    function handleCancelNew() {
      setNewLabelPosition(null);
    }

    const style: React.CSSProperties = {
      left: newScreenPos.x,
      top: newScreenPos.y - 10,
      transform: 'translate(-50%, -100%)',
    };

    return (
      <div className="ll-popup" style={style}>
        <div className="ll-popup__header">
          <span className="ll-popup__title">New Label</span>
          <button className="ll-popup__close" onClick={handleCancelNew} type="button">&times;</button>
        </div>

        <div className="ll-popup__field">
          <label>Label Text</label>
          <input
            value={newLabelText}
            onChange={(e) => setNewLabelText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && newLabelText.trim()) handleCreate(); }}
            placeholder="e.g. INV01, STR05..."
            autoFocus
          />
        </div>

        <div className="ll-popup__field">
          <label>Level</label>
          <div className="ll-popup__level-select">
            {allLevelOptions.map((opt) => {
              const c = getLevelColor(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`ll-popup__level-btn${newLabelLevel === opt.value ? ' ll-popup__level-btn--active' : ''}`}
                  style={{
                    borderColor: newLabelLevel === opt.value ? c : 'transparent',
                    color: newLabelLevel === opt.value ? c : 'var(--sl-text-muted)',
                  }}
                  onClick={() => setNewLabelLevel(opt.value)}
                >
                  <span className="ll-popup__level-dot" style={{ background: c }} />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="ll-popup__actions">
          <button
            className="ll-popup__btn ll-popup__btn--save"
            onClick={handleCreate}
            disabled={!newLabelText.trim()}
            type="button"
          >
            Add
          </button>
          <button className="ll-popup__btn ll-popup__btn--cancel" onClick={handleCancelNew} type="button">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Render: Multi-select summary popup ──
  if (selectedEntityIds.length > 1 && siteLayout?.transform && map) {
    // Position at centroid of selected entities
    const selectedEntities = siteLayout.textEntities.filter((e) => selectedEntityIds.includes(e.id));
    if (selectedEntities.length > 1) {
      let sumX = 0, sumY = 0;
      for (const e of selectedEntities) {
        const [lng, lat] = transformPoint(siteLayout.transform, e.cx, e.cy);
        sumX += lng;
        sumY += lat;
      }
      const centroidPt = map.project([sumX / selectedEntities.length, sumY / selectedEntities.length]);

      const multiStyle: React.CSSProperties = {
        left: centroidPt.x,
        top: centroidPt.y - 10,
        transform: 'translate(-50%, -100%)',
      };

      return (
        <div className="ll-popup" style={multiStyle}>
          <div className="ll-popup__header">
            <span className="ll-popup__title">{selectedEntities.length} Labels Selected</span>
            <button className="ll-popup__close" onClick={() => { selectLayoutEntities([]); selectLayoutEntity(null); }} type="button">&times;</button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--sl-text-muted, #9ca3af)', padding: '4px 0' }}>
            Drag any selected label to move all
          </div>
          {/* Batch typology change — one store write, applied as manual
              overrides so a re-classify never reverts them (owner request
              2026-09-01, OCR-cleanup workflow). */}
          <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', margin: '2px 0 4px' }}>
            <span style={{ fontSize: 9, color: 'var(--sl-text-muted, #9ca3af)', marginRight: 2 }}>
              Set all to:
            </span>
            {allLevelOptions.map((opt) => {
              const c = getLevelColor(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  style={{
                    padding: '1px 5px', fontSize: 9, borderRadius: 3, cursor: 'pointer',
                    background: 'transparent',
                    border: `1px solid ${c}66`,
                    color: c,
                  }}
                  onClick={() => {
                    batchSetLabelLevel([...selectedEntityIds], opt.value);
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {/* Batch module-grid overrides — the string subset of the selection
              gets the same per-string controls the single popup offers,
              applied in ONE store write (owner request 2026-09-02). */}
          {siteLayout?.moduleGridConfig && (() => {
            const stringIds = selectedEntities
              .filter((e) => (e.classificationOverride ?? e.classification)?.level === 'string')
              .map((e) => e.id);
            if (stringIds.length < 2) return null;
            const anyOverridden = stringIds.some((id) => siteLayout.stringOverrides?.[id]);
            return (
              <div className="ll-popup__field" style={{ marginTop: 4 }}>
                <label style={{ fontSize: 10, color: 'var(--sl-text-muted, #9ca3af)' }}>
                  Module Grid — {stringIds.length} strings
                </label>
                <StringGridControls
                  config={siteLayout.moduleGridConfig!}
                  mode="batch"
                  selectionOverrides={stringIds.map((id) => siteLayout.stringOverrides?.[id])}
                  onPatch={(patch) => batchPatchStringOverrides(stringIds, patch)}
                  onClear={anyOverridden ? () => batchRemoveStringOverrides(stringIds) : undefined}
                  clearLabel="Reset all"
                />
              </div>
            );
          })()}

          <div className="ll-popup__actions">
            <button
              className="ll-popup__btn ll-popup__btn--delete"
              onClick={() => {
                const ids = [...selectedEntityIds];
                showConfirm({
                  title: 'Delete labels',
                  message: `Delete ${ids.length} selected labels? This cannot be undone.`,
                  confirmLabel: 'Delete',
                  cancelLabel: 'Cancel',
                  variant: 'danger',
                  onConfirm: () => {
                    removeTextEntities(ids);
                    selectLayoutEntities([]);
                    selectLayoutEntity(null);
                  },
                });
              }}
              type="button"
            >
              Delete All ({selectedEntities.length})
            </button>
          </div>
        </div>
      );
    }
  }

  // ── Render: Edit existing label popup ──
  if (!entity || !screenPos || !entityId) return null;

  const level = entity.classification?.level ?? 'unknown';
  const levelColor = getLevelColor(level);

  function handleSave() {
    if (!entityId || editText === entity!.normalizedText) return;
    // Re-classify; if no match, keep existing classification with updated value
    const newClassification = classifyLabel(editText, siteLayout?.classificationConfig)
      ?? { level: entity!.classification?.level ?? 'unknown', value: editText };
    updateTextEntity(entityId, {
      normalizedText: editText,
      text: editText,
      classification: newClassification,
    });
  }

  function handleDelete() {
    if (!entityId) return;
    const id = entityId;
    showConfirm({
      title: 'Delete label',
      message: `Delete the label "${entity!.normalizedText}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
      onConfirm: () => {
        removeTextEntity(id);
        selectLayoutEntity(null);
      },
    });
  }

  function handleMove() {
    setLayoutLabelMoveMode(true);
  }

  function handleClose() {
    selectLayoutEntity(null);
  }

  const style: React.CSSProperties = {
    left: screenPos.x,
    top: screenPos.y - 10,
    transform: 'translate(-50%, -100%)',
  };

  return (
    <div className="ll-popup" style={style}>
      <div className="ll-popup__header">
        <span className="ll-popup__title">Edit Label</span>
        <button className="ll-popup__close" onClick={handleClose} type="button">&times;</button>
      </div>

      <span className="ll-popup__level" style={{ background: levelColor + '22', color: levelColor, border: `1px solid ${levelColor}44` }}>
        {level}{entity.classificationOverride ? ' (override)' : ''}
      </span>

      {/* Full identity: hierarchy fields carried by this label — including
          parents inherited via spatial binding (bare "17" → its DCCB heading) */}
      {(() => {
        const cls = entity.classificationOverride ?? entity.classification;
        const h = cls?.hierarchy as Record<string, string> | undefined;
        if (!h) return null;
        const ORDER = ['station', 'inverter', 'mppt', 'dccb', 'string', 'module'];
        const keys = [
          ...ORDER.filter((f) => h[f]),
          ...Object.keys(h).filter((f) => h[f] && !ORDER.includes(f)),
        ];
        if (keys.length === 0) return null;
        const parent = cls?.parentEntityId
          ? siteLayout?.textEntities.find((e) => e.id === cls.parentEntityId)
          : undefined;
        return (
          <div className="ll-popup__identity">
            <div className="ll-popup__id-row">
              {keys.map((f, i) => (
                <span key={f} className="ll-popup__id-part" title={f}>
                  {i > 0 && <span className="ll-popup__id-sep">·</span>}
                  <span style={{ color: getLevelColor(f) }}>{h[f]}</span>
                </span>
              ))}
            </div>
            {parent && (
              <div className="ll-popup__id-source">
                parents from nearest heading &ldquo;{parent.normalizedText}&rdquo;
              </div>
            )}
          </div>
        );
      })()}

      {/* Per-entity classification override */}
      {!moveMode && (
        <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
          {allLevelOptions.map((opt) => {
            const c = getLevelColor(opt.value);
            const isActive = level === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                style={{
                  padding: '1px 5px', fontSize: 9, borderRadius: 3, cursor: 'pointer',
                  background: isActive ? c + '33' : 'transparent',
                  border: `1px solid ${isActive ? c : 'rgba(255,255,255,0.12)'}`,
                  color: isActive ? c : 'var(--sl-text-muted, #9ca3af)',
                }}
                onClick={() => {
                  if (!entityId) return;
                  const override = { level: opt.value, value: entity!.normalizedText };
                  updateTextEntity(entityId, {
                    classificationOverride: override,
                    classification: override,
                  });
                }}
              >
                {opt.label}
              </button>
            );
          })}
          {entity.classificationOverride && (
            <button
              type="button"
              style={{
                padding: '1px 5px', fontSize: 9, borderRadius: 3, cursor: 'pointer',
                background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
                color: '#f87171',
              }}
              onClick={() => {
                if (!entityId) return;
                const autoClassification = classifyLabel(entity!.normalizedText, siteLayout?.classificationConfig) ?? undefined;
                updateTextEntity(entityId, {
                  classificationOverride: undefined,
                  classification: autoClassification,
                });
              }}
            >
              Auto
            </button>
          )}
        </div>
      )}

      {entity.text !== entity.normalizedText && (
        <div className="ll-popup__original">Original: {entity.text}</div>
      )}

      <div className="ll-popup__field">
        <label>Label Text</label>
        <input
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
        />
      </div>

      {moveMode && (
        <div className="ll-popup__move-hint">
          Click on the map to place this label at a new position
        </div>
      )}

      {/* Per-string module grid override — FULL control set (shared with the
          batch section; owner request 2026-09-02). */}
      {level === 'string' && siteLayout?.moduleGridConfig && entityId && (() => {
        const override = siteLayout.stringOverrides?.[entityId];
        const config = siteLayout.moduleGridConfig!;
        const movedCount = override?.modulePositions ? Object.keys(override.modulePositions).length : 0;

        const patchOverride = (patch: Partial<StringOverride>) => {
          const next = { ...override, ...patch };
          // every field back at its default ⇒ drop the override entirely
          const empty = Object.values(next).every((v) => v === undefined);
          setStringOverride(entityId, empty ? null : next);
        };

        const setFieldCount = override
          ? Object.entries(override).filter(([k, v]) => v !== undefined && k !== 'modulePositions').length
          : 0;

        return (
          <div className="ll-popup__field" style={{ marginTop: 4 }}>
            <button
              type="button"
              className="llg-sec-toggle"
              onClick={() => setGridOpen((o) => !o)}
            >
              <span>{gridOpen ? '▾' : '▸'} Module Grid Override</span>
              {!gridOpen && (setFieldCount > 0 || movedCount > 0) && (
                <span className="llg-sec-badge">
                  {setFieldCount > 0 ? `${setFieldCount} set` : ''}
                  {setFieldCount > 0 && movedCount > 0 ? ' · ' : ''}
                  {movedCount > 0 ? `${movedCount} moved` : ''}
                </span>
              )}
            </button>
            {gridOpen && (
            <StringGridControls
              config={config}
              mode="single"
              override={override}
              entityRotation={entity!.rotation}
              onPatch={patchOverride}
              onClear={override ? () => removeStringOverride(entityId) : undefined}
            />
            )}
            {/* Module position overrides */}
            {gridOpen && movedCount > 0 && (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 4 }}>
                <span style={{ fontSize: 10, color: 'var(--sl-accent, #13d6c6)' }}>
                  {movedCount} module{movedCount > 1 ? 's' : ''} repositioned
                </span>
                <button
                  type="button"
                  style={{ marginLeft: 'auto', padding: '1px 6px', fontSize: 9, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 3, color: '#ef4444', cursor: 'pointer' }}
                  onClick={() => {
                    if (!override) return;
                    const { modulePositions: _, ...rest } = override;
                    const hasOther = Object.values(rest).some((v) => v !== undefined);
                    setStringOverride(entityId, hasOther ? rest : null);
                  }}
                >
                  Reset Positions
                </button>
              </div>
            )}
          </div>
        );
      })()}

      <div className="ll-popup__actions">
        {editText !== entity.normalizedText && (
          <button className="ll-popup__btn ll-popup__btn--save" onClick={handleSave} type="button">Save</button>
        )}
        {!moveMode && (
          <button className="ll-popup__btn ll-popup__btn--move" onClick={handleMove} type="button">Move</button>
        )}
        <button className="ll-popup__btn ll-popup__btn--delete" onClick={handleDelete} type="button">Delete</button>
      </div>
    </div>
  );
}
